/**
 * "Somebody from Rowted is in your account" — plan §6.12.
 *
 * Persistent, above everything, and not dismissible. That is the point: it is
 * not a notification, it is a statement about the state of the account, and a
 * customer who can hide it is a customer who might forget. §6.12: "visible to
 * the tenant in a persistent banner and its audit log."
 *
 * It says who, why, until when, and whether they can change anything — all
 * four, because a banner that says only "support is here" leaves the customer
 * to imagine the rest. And Stop is theirs to press: read-only or not, the
 * person whose account it is can end it (the server allows that one mutation
 * on a read-only session for exactly this reason).
 */
import { useEffect, useState } from 'react'
import { ApiError, type MeResponse } from '@rowted/shared'
import { request } from '../lib/api.js'
import { Button } from '../components/primitives.js'

type Support = NonNullable<MeResponse['impersonation']>

/** `in 42 minutes`, and `expired` once the clock runs out. */
function remaining(expiresAt: string): string {
  const minutes = Math.round((new Date(expiresAt).getTime() - Date.now()) / 60_000)
  if (minutes <= 0) return 'ending now'
  if (minutes === 1) return 'for another minute'
  if (minutes < 60) return `for another ${minutes} minutes`
  const hours = Math.round(minutes / 60)
  return `for another ${hours} ${hours === 1 ? 'hour' : 'hours'}`
}

export function SupportBanner({ support }: { support: Support }) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // Re-renders once a minute so "for another 42 minutes" does not go stale.
  const [, tick] = useState(0)

  useEffect(() => {
    const timer = window.setInterval(() => tick((value) => value + 1), 60_000)
    return () => window.clearInterval(timer)
  }, [])

  async function stop() {
    setBusy(true)
    setError(null)
    try {
      /*
       * The server says where to go, because only it knows the front door's
       * host — item 7. For a Rowted employee that is the client's record in
       * Rowted Admin, where their own session has been live the whole time;
       * `/sign-in` sent them to this company's door instead and asked them to
       * sign in somewhere they have no account.
       */
      const answer = await request<{ returnTo: string | null }>(
        '/me/impersonation/end', { method: 'POST', body: {} })
      // The session is revoked, so there is nothing left to render as.
      window.location.assign(answer?.returnTo ?? '/sign-in')
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'That could not be stopped.')
      setBusy(false)
    }
  }

  return (
    <div
      role="status"
      data-support-banner
      className="flex shrink-0 flex-wrap items-center gap-x-2 gap-y-1 border-b border-warn
                 bg-warn-bg px-4 py-1.5 text-cell text-warn"
    >
      <span className="font-semibold">
        {support.actorName} from Rowted is signed in as you
      </span>
      <span className="text-warn/80">
        · {support.readOnly ? 'looking only' : 'can make changes'}
        {' · '}{remaining(support.expiresAt)}
        {' · '}{support.reason}
      </span>
      {error && <span className="font-semibold text-danger">{error}</span>}
      <Button className="ml-auto" disabled={busy} onClick={() => void stop()}>
        {busy ? 'Stopping…' : 'Stop'}
      </Button>
    </div>
  )
}
